import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

import { ContactDetail, formatMoment, personName } from '../contactDetail';
import { CRM_ADD_CONTACT_NOTE, CRM_DELETE_CONTACT_NOTE, CRM_UPDATE_CONTACT_NOTE } from './contactEdit.queries';
import { useContactEdit } from './useContactEdit';

const NOTE_MAX = 5000;

/** Write a note, or change one: in place, with Save and Cancel. Ctrl+Enter saves. */
const NoteEditor = ({
  initial,
  saving,
  error,
  onSave,
  onCancel,
}: {
  initial: string;
  saving: boolean;
  error: { message: string } | null;
  onSave: (body: string) => void;
  onCancel: () => void;
}) => {
  const [body, setBody] = useState(initial);
  const ready = body.trim() !== '' && body.trim() !== initial.trim();
  return (
    <Box sx={{ display: 'grid', gap: 1 }}>
      <TextField
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="Prefers WhatsApp after 6pm. Daughter answers the home number."
        multiline
        minRows={3}
        autoFocus
        inputProps={{ maxLength: NOTE_MAX, 'aria-label': 'Note' }}
        helperText={body.length > NOTE_MAX * 0.9 ? `${body.length.toLocaleString('en-US')} of ${NOTE_MAX.toLocaleString('en-US')}` : 'Ctrl+Enter saves.'}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && (event.ctrlKey || event.metaKey) && ready && !saving) {
            event.preventDefault();
            onSave(body);
          }
          if (event.key === 'Escape') onCancel();
        }}
      />
      {error && <Alert severity="error">{error.message}</Alert>}
      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
        <Button size="small" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button
          size="small"
          variant="contained"
          onClick={() => onSave(body)}
          disabled={!ready || saving}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : undefined}
        >
          Save note
        </Button>
      </Box>
    </Box>
  );
};

/**
 * Notes on the person (card 21.5; card 16.5 showed them). Any admin writes one;
 * only its author changes or deletes it (`editable`). NationBuilder's notes are
 * kept as they were. Every note written, changed or deleted is on the timeline.
 */
export const ContactNotes = ({ contact, onChanged }: { contact: ContactDetail; onChanged: () => void }) => {
  const live = contact.mergedIntoId == null;
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<number | null>(null);
  const [deleting, setDeleting] = useState<number | null>(null);
  const add = useContactEdit(CRM_ADD_CONTACT_NOTE, () => {
    setAdding(false);
    onChanged();
  });
  const update = useContactEdit(CRM_UPDATE_CONTACT_NOTE, () => {
    setEditing(null);
    onChanged();
  });
  const remove = useContactEdit(CRM_DELETE_CONTACT_NOTE, () => {
    setDeleting(null);
    onChanged();
  });

  return (
    <Box sx={{ display: 'grid', gap: 1 }}>
      {contact.notes.length === 0 && !adding && (
        <Typography variant="body2" color="text.secondary">
          No notes yet.
        </Typography>
      )}
      {adding && (
        <NoteEditor initial="" saving={add.loading} error={add.error} onSave={(body) => add.run({ contactId: contact.id, body })} onCancel={() => setAdding(false)} />
      )}
      <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, display: 'grid', gap: 1 }}>
        {contact.notes.map((note) => (
          <Box
            component="li"
            key={note.id}
            sx={{ bgcolor: '#fffbeb', border: '1px solid #fde68a', borderRadius: 1.5, px: 1.25, py: 1 }}
          >
            {editing === note.id ? (
              <NoteEditor
                initial={note.body}
                saving={update.loading}
                error={update.error}
                onSave={(body) => update.run({ noteId: note.id, body })}
                onCancel={() => setEditing(null)}
              />
            ) : (
              <>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {note.body}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5, mt: 0.25 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mr: 'auto' }}>
                    {/* A migrated note has no real date: NationBuilder kept none (§19). */}
                    {note.source === 'NATIONBUILDER'
                      ? 'From NationBuilder'
                      : [personName(note.admin), formatMoment(note.createdAt), note.updatedAt && note.updatedAt !== note.createdAt ? 'edited' : '']
                          .filter(Boolean)
                          .join(' · ')}
                  </Typography>
                  {live && note.editable && (
                    <>
                      <Button size="small" sx={{ minWidth: 0, py: 0 }} onClick={() => setEditing(note.id)} aria-label="Edit this note">
                        Edit
                      </Button>
                      <Button size="small" color="error" sx={{ minWidth: 0, py: 0 }} onClick={() => setDeleting(note.id)} aria-label="Delete this note">
                        Delete
                      </Button>
                    </>
                  )}
                </Box>
              </>
            )}
          </Box>
        ))}
      </Box>
      {live && !adding && (
        <Button size="small" startIcon={<AddIcon />} onClick={() => setAdding(true)} sx={{ justifySelf: 'start', ml: -0.75 }}>
          Add a note
        </Button>
      )}
      {deleting != null && (
        <Dialog open onClose={remove.loading ? undefined : () => setDeleting(null)} maxWidth="xs" fullWidth aria-labelledby="crm-note-delete-title">
          <DialogTitle id="crm-note-delete-title">Delete this note?</DialogTitle>
          <DialogContent>
            <DialogContentText variant="body2">Its text stays on their timeline.</DialogContentText>
            {remove.error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {remove.error.message}
              </Alert>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 1.5 }}>
            <Button onClick={() => setDeleting(null)} disabled={remove.loading}>
              Cancel
            </Button>
            <Button variant="contained" color="error" disabled={remove.loading} onClick={() => remove.run({ noteId: deleting })}>
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};
